import { Divider } from "$root/components/Divider";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { type TagOption, TagOptionsEditor } from "$root/ui-lib/form/TagOptionsEditor";
import { CustomAttributeType } from "@eu.mdotm.api/custom-attributes";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgressBar, Column } from "@mdotm/shadcn/legacy/components";
import type { SelectOption } from "@mdotm/shadcn/mdotm/components";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import type { SubmittableHandle } from "./type";
import z from "zod";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";
import { useTranslation } from "react-i18next";

const availableProperties: SelectOption<CustomAttributeType>[] = [
	{
		label: "Text",
		value: CustomAttributeType.Text,
	},
	{
		label: "Number",
		value: CustomAttributeType.Number,
	},
	{
		label: "Date",
		value: CustomAttributeType.Date,
	},
	{
		label: "Tag",
		value: CustomAttributeType.Tag,
	},
];

const attributeErrorMessage: z.RawCreateParams = {
	errorMap(issue) {
		if (issue.code === "invalid_union_discriminator") {
			return { message: "Select a valid option" };
		}

		return { message: "Missing value" };
	},
};
const nameErrorMessage: z.RawCreateParams = { required_error: "Please provide a valid name" };
export const WorkspaceTableSettingsColumnPropertyResolver = z.discriminatedUnion(
	"attributeType",
	[
		z.object({
			attributeType: z.literal(CustomAttributeType.Date),
			name: z.string(nameErrorMessage),
			description: z.string().optional(),
		}),
		z.object({
			attributeType: z.literal(CustomAttributeType.Number),
			name: z.string(nameErrorMessage),
			description: z.string().optional(),
		}),
		z.object({
			attributeType: z.literal(CustomAttributeType.Text),
			name: z.string(nameErrorMessage),
			description: z.string().optional(),
		}),
		z.object({
			attributeType: z.literal(CustomAttributeType.Tag),
			name: z.string(nameErrorMessage),
			description: z.string().optional(),
			tagOptions: z.array(z.object({ label: z.string(), color: z.string() })).optional(),
			allowMultipleTags: z.boolean(),
		}),
	],
	attributeErrorMessage,
);
export type WorkspaceTableSettingsColumnProperty = z.infer<typeof WorkspaceTableSettingsColumnPropertyResolver>;

const WorkspaceTableSettingsColumnProperty = forwardRef<
	SubmittableHandle,
	{
		onSubmit(values: WorkspaceTableSettingsColumnProperty): void;
		defaultValues?: WorkspaceTableSettingsColumnProperty;
		readonlyAttributeType?: boolean;
		readonlyAllowMultipleTags?: boolean;
		/** Renders the whole form read-only (managed/system-shared concepts: configuration is not editable). */
		readonly?: boolean;
		onBeforeRemoveTagOption?: (
			option: TagOption,
			form: UseFormReturn<WorkspaceTableSettingsColumnProperty>,
		) => Promise<boolean>;
		/**
		 * Rename one tag option through the rename endpoint, rather than by editing the form.
		 *
		 * Rejecting vetoes the rename: the option keeps the name it had. Absent, the name stays read-only —
		 * an option renamed in the form alone reaches the update endpoint as a delete plus a create.
		 */
		onRenameTagOption?: (currentTagValue: string, newTagValue: string) => Promise<void>;
	}
>(function CreateColumnProperty(
	{
		onSubmit,
		defaultValues,
		readonlyAttributeType,
		readonlyAllowMultipleTags,
		readonly,
		onBeforeRemoveTagOption,
		onRenameTagOption,
	},
	ref,
) {
	const { t } = useTranslation();
	const { checkIfNameIsAvailable, checkingNameUniqueness } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: investmentCustomAttributesRepository.checkNameAvailability.fetchQuery,
		currentName: null,
	});

	const form = useForm<WorkspaceTableSettingsColumnProperty>({
		defaultValues,
		resolver: zodResolver(
			WorkspaceTableSettingsColumnPropertyResolver.refine(
				(item) => {
					if (defaultValues?.name && item.name === defaultValues?.name) {
						return true;
					}

					const isNameAvailable = checkIfNameIsAvailable(item.name);
					return isNameAvailable;
				},
				{
					message: t("MISSING_FIELD.NAME_NOT_AVAILABLE"),
					path: ["name"],
				},
			),
		),
	});

	useImperativeHandle(ref, () => ({
		submit: () =>
			handleSubmitToCustomSubmitHandler({
				handleSubmit: form.handleSubmit,
				baseSubmitFn: onSubmit,
			})(),
	}));

	const observed = form.watch();
	const isTag = observed.attributeType === CustomAttributeType.Tag;

	const tagOptions: TagOption[] = useMemo(() => {
		if (observed.attributeType !== "TAG") {
			return [];
		}
		const formOptions = observed.tagOptions ?? [];
		return formOptions.map((opt) => ({
			option: opt.label,
			color: opt.color as TagOption["color"],
		}));
	}, [observed]);

	return (
		<Column minSize="0" gap={16}>
			<FormFields.Select
				label="Property type"
				control={form.control}
				formState={form.formState}
				name="attributeType"
				options={availableProperties}
				disabled={readonlyAttributeType || readonly}
				onChange={(newPropertyType: CustomAttributeType | null) => {
					if (
						newPropertyType &&
						newPropertyType === CustomAttributeType.Tag &&
						!form.getFieldState("allowMultipleTags").isDirty
					) {
						form.setValue("allowMultipleTags", true);
					}
				}}
			/>
			{isTag && (
				<FormFields.Checkbox
					name="allowMultipleTags"
					control={form.control}
					formState={form.formState}
					className="flex-row-reverse gap-2 w-fit"
					disabled={readonlyAllowMultipleTags || readonly}
				>
					Allow multiple tags
				</FormFields.Checkbox>
			)}
			{isTag && (
				<>
					<Divider />
					{/*
					  The options list scrolls inside this box: TagOptionsEditor gives it `flex-1 min-h-0 overflow-auto`,
					  which bounds it only as far as this box is bounded. The shrink reaches here from SidePanelContent
					  through the Columns between, each of which carries `min-h-0` to let it through. A Column does
					  not shrink by default: its `min-height: auto` is what makes a list push a scroll container to
					  scroll, so the floor is dropped one site at a time, never globally.
					*/}
					<div className="min-h-0 flex flex-col max-h-[420px]">
						<TagOptionsEditor
							value={tagOptions}
							onChange={(options) => {
								const mapped = options.map((opt) => ({ label: opt.option, color: opt.color ?? "GRAY" }));
								form.setValue("tagOptions", mapped);
							}}
							allowAddOption={!readonly}
							allowRemoveOption={!readonly}
							onRemoveOptionAsync={
								onBeforeRemoveTagOption
									? async (option, index) => {
											const proceed = await onBeforeRemoveTagOption(option, form);
											if (!proceed) {
												return;
											}
											const current = form.getValues("tagOptions") ?? [];
											form.setValue(
												"tagOptions",
												current.filter((_, i) => i !== index),
											);
										}
									: undefined
							}
							allowEditOptionName={!readonly}
							onRenameOption={onRenameTagOption}
							allowEditOptionOrder={!readonly}
							allowEditOptionColor={!readonly}
							allowSpecialCharacters
						/>
					</div>
				</>
			)}
			<Divider />

			<FormFields.Text
				label="Property name"
				control={form.control}
				formState={form.formState}
				name="name"
				disabled={readonly}
				rightContent={
					checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
				}
			/>
			{/* <FormFields.TextArea
					label="Description"
					control={form.control}
					formState={form.formState}
					name="description"
					placeholder="Add a description to easily identify this item when browsing the list. This text will be displayed on the list."
				/> */}
		</Column>
	);
});

export default WorkspaceTableSettingsColumnProperty;
